import React, {useEffect, useRef, useState} from 'react';
import styled, {css} from 'styled-components';
import {black, inter} from '../../Theme';
import {skills} from '../../constants/skills';
import {Skills} from '../../constants/enums';
import { ScoreIndicatorIcon } from '../../screens/planofaction/PlanOfActionOverview';
import { SingleBar } from './SingleBar';
import { device } from '../../constants/breakpoints';

const StyledProgressBarChart = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 48px 32px;

    ${({$pdfExport}) => !$pdfExport && css`
        @media screen and ${device.laptopL} {
            padding: 32px 32px 32px 20px;
        }

        @media screen and ${device.mobileL} {
            padding: 24px 24px 24px 16px;
        }
    `}
`;

const ChartGrid = styled.div`
    flex: 1;
    display: grid;
    grid-template-columns: 30px min-content 1fr; // 30px = IndicatorColumn width
    grid-template-rows: minmax(0, 1fr) auto;
    min-height: 0;
    width: 100%;
`;

const IndicatorColumn = styled.div`
    position: relative;
    width: 30px;
    grid-column: 1;
    grid-row: 1;
`;

const StyledScoreIndicatorIcon = styled(ScoreIndicatorIcon)`
    position: absolute;
    left: 0;
    bottom: ${({$bottom}) => $bottom}%;
    transform: translateY(50%);
`;

const YAxis = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding-right: 10px;
    grid-column: 2;
    grid-row: 1;
`;

const YMajorTick = styled.span`
    font-family: ${inter};
    font-size: 12px;
    color: ${black};
    text-align: right;
    line-height: 0;
`;

const ChartBody = styled.div`
    position: relative;
    min-width: 0;
    grid-column: 3;
    grid-row: 1;
`;

const GridLines = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column-reverse;
    justify-content: space-between;
    pointer-events: none;
`;

const GridLine = styled.div`
    width: 100%;
    height: 1px;
    background: rgba(0, 0, 0, 0.15);
`;

const Bars = styled.div`
    height: 100%;
    display: flex;
`;

const LabelsFlex = styled.div`
    grid-column: 3;
    grid-row: 2;
    display: flex;
    overflow: visible;
    min-width: 0;
`;

const Labels = styled.div`
    flex: 1;
    display: flex;
    min-width: 0;
`;

const LabelSlot = styled.div`
    flex: 1;
    position: relative;
    min-width: 0;
`;

const BarLabel = styled.span`
    font-family: ${inter};
    font-weight: 600;
    font-size: 14px;
    color: ${black};
    white-space: nowrap;
    transform: rotate(-45deg);
    transform-origin: top right;
    position: absolute;
    top: 6px;
    right: 50%;

    ${({$pdfExport}) => !$pdfExport && css`
        @container (max-width: 500px) { font-size: 12px; }
        @container (max-width: 400px) { font-size: 10px; }
        @container (max-width: 300px) { font-size: 8px; }
        @container (max-width: 260px) { font-size: 6px; }
    `}
`;

const PreviewRoot = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    min-height: 0;
`;

const PreviewChartBody = styled.div`
    position: relative;
    flex: 1;
    min-height: 0;
    min-width: 0;
    width: 100%;
`;

const PreviewBars = styled(Bars)`
    height: 100%;
`;

export const ProgressBarChart = ({data, variant = "full", previewEmpty = false, pdfExport = false}) => {
    const chartData = data?.scoresPerExecutiveFunction;
    const yMax = data?.mastered;
    const yMajorTicks = Array.from({length: yMax / 2 + 1}, (_, i) => i * 2);
    const gridLines = Array.from({length: yMax + 1}, (_, i) => i);

    const labelsRef = useRef(null);
    const [labelHeight, setLabelHeight] = useState(0);

    useEffect(() => {
        if (variant === "preview" || !labelsRef.current) return;

        const calculateLabelHeight = () => {
          const labels = labelsRef.current.querySelectorAll('span');
          let maxHeight = 0;
          labels.forEach((label) => {
            maxHeight = Math.max(maxHeight, label.getBoundingClientRect().height);
          });
          setLabelHeight(Math.ceil(maxHeight) + 6); // + 6 because BarLabel has `top: 6px`
        };

        calculateLabelHeight();

        const observer = new ResizeObserver(calculateLabelHeight);
        observer.observe(labelsRef.current);

        return () => {
            observer.disconnect()
        };
      }, [chartData, variant]);

    const indicators = [
        {skill: skills[Skills.MASTERED], position: ((data?.developing + 1) / yMax) * 100},
        {skill: skills[Skills.DEVELOPING], position: ((data?.beginner + 1) / yMax) * 100},
        {skill: skills[Skills.BEGINNER], position: 0},
    ];

    if (variant === "preview") {
        return (
            <PreviewRoot>
                <PreviewChartBody>
                    <GridLines>
                        {gridLines.map(tick => (
                            <GridLine key={tick} />
                        ))}
                    </GridLines>
                    {!previewEmpty && (
                        <PreviewBars>
                            {chartData.map((item, index) => (
                                <SingleBar key={item.id} item={item} yMax={yMax} barIndex={index} />
                            ))}
                        </PreviewBars>
                    )}
                </PreviewChartBody>
            </PreviewRoot>
        );
    }

    if (!chartData?.length) return null;

    return (
        <StyledProgressBarChart $pdfExport={pdfExport}>
            <ChartGrid>
                <IndicatorColumn>
                    {indicators.map((indicator) => (
                        <StyledScoreIndicatorIcon 
                            key={indicator.skill.key} 
                            determinedSkill={indicator.skill} 
                            pdfExport={pdfExport}
                            $bottom={indicator.position}
                        />
                    ))}
                </IndicatorColumn>
                <YAxis>
                    {yMajorTicks.slice().reverse().map(tick => (
                        <YMajorTick key={tick}>{tick}</YMajorTick>
                    ))}
                </YAxis>
                <ChartBody>
                    <GridLines>
                        {gridLines.map(tick => (
                            <GridLine key={tick} />
                        ))}
                    </GridLines>
                    <Bars>
                        {chartData.map((item, index) => (
                            <SingleBar key={item.id} item={item} yMax={yMax} barIndex={index} pdfExport={pdfExport} />
                        ))}
                    </Bars>
                </ChartBody>
                <LabelsFlex style={{height: labelHeight}}>
                    <Labels ref={labelsRef}>
                        {chartData.map(item => (
                            <LabelSlot key={item.id}>
                                <BarLabel $pdfExport={pdfExport}>{item.title}</BarLabel>
                            </LabelSlot>
                        ))}
                    </Labels>
                </LabelsFlex>
            </ChartGrid>
        </StyledProgressBarChart>
    );
};